import type { ToolSet } from '@internal/ai-sdk-v5';
import { z } from 'zod/v4';
import { stopGoalActivity } from '../../../agent/goal';
import { resolveDeclineReason } from '../../../agent/tool-approval';
import { executeAdoptedBackgroundOperation } from '../../../background-tasks/adoption';
import type { BackgroundTaskProgressChunk, ToolBackgroundConfig } from '../../../background-tasks/types';
import type { MastraDBMessage } from '../../../memory';
import { BACKGROUND_WORK_CONTEXT, notifyBackgroundWorkTerminal } from '../../../processors/background-work-signals';
import { toStandardSchema, standardSchemaToJSONSchema } from '../../../schema';
import { safeEnqueue } from '../../../stream/base';
import { ChunkFrom } from '../../../stream/types';
import type { ChunkType, ProviderMetadata } from '../../../stream/types';
import {
  getTransformedToolPayload,
  hasTransformedToolPayload,
  withToolPayloadTransformProviderMetadata,
} from '../../../tools/payload-transform';
import { findProviderToolByName } from '../../../tools/provider-tool-utils';
import { getToolTitle } from '../../../tools/tool-title';
import type { MastraToolInvocationOptions } from '../../../tools/types';
import { resolveToolOutputValidationSchema, validateToolOutput } from '../../../tools/validation';
import { ensureSerializable } from '../../../utils';
import type { SuspendOptions } from '../../../workflows/step';
import { createStep } from '../../../workflows/workflow';
import type { RunScopeContext } from '../../run-scope-access';
import { readScoped, writeScoped } from '../../run-scope-access';
import {
  AGENT_BACKGROUND_CONFIG_KEY,
  BACKGROUND_TASK_MANAGER_CONFIG_KEY,
  BACKGROUND_TASK_MANAGER_KEY,
  EAGER_TOOL_EXECUTION_KEY,
  GENERATE_ID_KEY,
  MEMORY_CONFIG_KEY,
  MEMORY_KEY,
  NOW_KEY,
  RESOURCE_ID_KEY,
  SAVE_QUEUE_MANAGER_KEY,
  STEP_ACTIVE_TOOLS_KEY,
  STEP_MODEL_MESSAGES_KEY,
  STEP_TOOLS_KEY,
  STEP_WORKSPACE_KEY,
  THREAD_EXISTS_KEY,
  THREAD_ID_KEY,
  TOOL_APPROVAL_VERDICTS_KEY,
  TOOL_PAYLOAD_TRANSFORM_KEY,
} from '../../run-scope-keys';
import { dispatchBackgroundTool } from '../../shared/steps/background-dispatch-core';
import { applyBackgroundToolResult } from '../../shared/steps/background-task-result-core';
import { executeToolCall } from '../../shared/steps/execute-tool-core';
import { resolveFrameworkSuspendedToolIdentity } from '../../shared/suspended-tool-run-id';
import type { ResolvedSuspendedToolIdentity } from '../../shared/suspended-tool-run-id';
import { applyToolPayloadTransformToChunk } from '../../shared/tool-payload-transform';
import type { OuterLLMRun } from '../../types';
import { serializeToolError, ToolNotFoundError } from '../errors';
import { toolCallInputSchema, toolCallOutputSchema } from '../schema';
import {
  EAGER_TOOL_ABORT_SIGNAL,
  EAGER_TOOL_BAILOUT,
  EAGER_TOOL_EXECUTION_MARKER,
  eagerToolCallAlreadyAnnouncedInput,
  EagerToolExecutionNotRun,
  eagerToolCallDidNotExecute,
  eagerToolCallSuspensionIntent,
} from './eager-tool-execution';
import type { EagerSuspensionIntent, EagerToolBailout } from './eager-tool-execution';
import { buildToolApprovalContext, resolveToolApprovalVerdict } from './tool-approval-verdict';

type AddToolMetadataOptions = {
  toolCallId: string;
  toolName: string;
  args: unknown;
  parentToolName?: string;
  parentArgs?: unknown;
  resumeSchema: string;
  suspendedToolRunId?: string;
  metadata?: Record<string, unknown>;
} & (
  | {
      type: 'approval';
      suspendPayload?: never;
    }
  | {
      type: 'suspension';
      suspendPayload: unknown;
    }
);

export function createToolCallStep<Tools extends ToolSet = ToolSet, OUTPUT = undefined>({
  tools,
  messageList,
  options,
  outputWriter,
  controller,
  runId,
  streamState,
  modelSpanTracker,
  _internal,
  logger,
  agentId,
  agentVersionId,
  mastra,
  requireToolApproval: requireToolApprovalFromFactory,
  actor,
  mcp,
}: OuterLLMRun<Tools, OUTPUT>) {
  return createStep({
    id: 'toolCallStep',
    inputSchema: toolCallInputSchema,
    outputSchema: toolCallOutputSchema,
    execute: async executionContext => {
      const { inputData, suspend, resumeData: workflowResumeData, suspendData, requestContext } = executionContext;
      // Eager dispatch invokes this step with its own signal, chained to the run's, so a
      // call started for a model attempt that is later discarded can be cancelled on its
      // own. Every other caller falls back to the run signal, unchanged.
      const callerAbortSignal = (executionContext as unknown as Record<symbol, AbortSignal | undefined>)[
        EAGER_TOOL_ABORT_SIGNAL
      ];
      const abortSignal =
        callerAbortSignal && options?.abortSignal
          ? AbortSignal.any([callerAbortSignal, options.abortSignal])
          : (callerAbortSignal ?? options?.abortSignal);
      // Resolve run-scoped state from either the Mastra-managed RunScope (production
      // path via loop.ts hydration) or the legacy `_internal` bag (tests).
      const scopeCtx: RunScopeContext = { mastra, runId, _internal };
      const isEagerExecution = Boolean((executionContext as any)[EAGER_TOOL_EXECUTION_MARKER]);
      // Present only on an eager dispatch. Marked before bailing out, so the dispatcher
      // can reject a settlement whose bailout the tool swallowed.
      const eagerBailout = (executionContext as unknown as Record<symbol, EagerToolBailout | undefined>)[
        EAGER_TOOL_BAILOUT
      ];
      // Adopt an execution the LLM step started eagerly for this call, if any. The
      // eager invocation itself carries the marker so it never adopts itself.
      // Set when the eager attempt this invocation replaces already ran the tool's
      // `onInputAvailable`, so the hook stays at one call per adoption.
      let inputAlreadyAnnouncedEagerly = false;
      // Set when the eager attempt was abandoned because the tool suspended at runtime.
      // Stashed here and consumed further down, where the suspension helper's dependencies
      // (`args`, `transformChunk`, `flushMessagesBeforeSuspension`) exist.
      let eagerSuspensionIntent: EagerSuspensionIntent | undefined;
      if (!isEagerExecution) {
        // Take rather than read: adoption is exactly-once, so a later iteration that
        // reuses this toolCallId executes again instead of replaying a stale result.
        const eagerExecution = readScoped(scopeCtx, EAGER_TOOL_EXECUTION_KEY, 'eagerToolExecutionCoordinator')?.take(
          inputData.toolCallId,
        );
        if (eagerExecution) {
          try {
            return (await eagerExecution) as any;
          } catch (error) {
            // The eager attempt produced nothing adoptable: it was cancelled while
            // still queued, or it turned out to need suspension. Run it normally
            // instead. In the suspension case the body did start, so the hook it
            // already announced must not be announced a second time.
            if (!eagerToolCallDidNotExecute(error)) throw error;
            inputAlreadyAnnouncedEagerly = eagerToolCallAlreadyAnnouncedInput(error);
            eagerSuspensionIntent = eagerToolCallSuspensionIntent(error);
          }
        }
      }
      // Use tools from the scope (set by llmExecutionStep via prepareStep/processInputStep)
      // when available. This avoids serialization — execute functions live off-the-wire.
      // Fall back to the original tools from the closure if not set.
      const stepTools = (readScoped(scopeCtx, STEP_TOOLS_KEY, 'stepTools') as Tools | undefined) || tools;
      const stepActiveTools = readScoped(scopeCtx, STEP_ACTIVE_TOOLS_KEY, 'stepActiveTools');
      const tool =
        stepTools?.[inputData.toolName] ||
        findProviderToolByName(stepTools, inputData.toolName) ||
        Object.values(stepTools || {})?.find((t: any) => `id` in t && t.id === inputData.toolName);
      const transformSource = {
        policy: readScoped(scopeCtx, TOOL_PAYLOAD_TRANSFORM_KEY, 'toolPayloadTransform'),
        toolTransform: (tool as { transform?: unknown } | undefined)?.transform as any,
      };
      const transformChunk = async (chunk: ChunkType<OUTPUT>): Promise<ChunkType<OUTPUT>> =>
        applyToolPayloadTransformToChunk(chunk as ChunkType<OUTPUT> & { payload?: any }, {
          policy: transformSource.policy,
          toolTransform: transformSource.toolTransform,
          logger,
          transformInput: {
            providerMetadata: inputData.providerMetadata as Record<string, unknown> | undefined,
          },
        }) as Promise<ChunkType<OUTPUT>>;

      const addToolMetadata = ({
        toolCallId,
        toolName,
        args,
        parentToolName,
        parentArgs,
        suspendPayload,
        resumeSchema,
        type,
        suspendedToolRunId,
        metadata: toolStateTransformMetadata,
      }: AddToolMetadataOptions) => {
        const metadataKey = type === 'suspension' ? 'suspendedTools' : 'pendingToolApprovals';
        const inputTransform = getTransformedToolPayload(toolStateTransformMetadata, 'transcript', 'input-available');
        const approvalTransform = getTransformedToolPayload(toolStateTransformMetadata, 'transcript', 'approval');
        const suspendTransform = getTransformedToolPayload(toolStateTransformMetadata, 'transcript', 'suspend');
        const transformedArgs =
          type === 'approval'
            ? hasTransformedToolPayload(approvalTransform)
              ? approvalTransform.transformed
              : hasTransformedToolPayload(inputTransform)
                ? inputTransform.transformed
                : args
            : hasTransformedToolPayload(inputTransform)
              ? inputTransform.transformed
              : hasTransformedToolPayload(suspendTransform)
                ? suspendTransform.transformed
                : args;
        const transformedSuspendPayload =
          type === 'suspension'
            ? hasTransformedToolPayload(suspendTransform)
              ? suspendTransform.transformed
              : suspendPayload
            : undefined;
        const entry = {
          toolCallId,
          toolName,
          args: transformedArgs,
          ...(parentToolName ? { parentToolName, parentArgs } : {}),
          type,
          // Store the OUTER (resumable) runId so clients can resume after page refresh or
          // server restart via `resumeStream({ runId, toolCallId })`. For delegated sub-agent /
          // workflow tools the inner suspended run is preserved separately as `delegatedRunId`
          // — it is required to resume the delegate's own suspended stream, but it is not a
          // valid public resume target (resuming with it fails closed). No `parentRunId` is
          // written: readers that resume `parentRunId ?? runId` (channels) get the outer run
          // from `runId` directly; legacy entries with `parentRunId` keep working.
          runId,
          ...(suspendedToolRunId && suspendedToolRunId !== runId ? { delegatedRunId: suspendedToolRunId } : {}),
          ...(type === 'suspension' ? { suspendPayload: transformedSuspendPayload } : {}),
          resumeSchema,
          ...(toolStateTransformMetadata ? { metadata: toolStateTransformMetadata } : {}),
        };
        const carriesToolCall = (message: MastraDBMessage) =>
          message.role === 'assistant' &&
          (message.content?.parts ?? []).some(
            part => part.type === 'tool-invocation' && part.toolInvocation.toolCallId === toolCallId,
          );

        const responseMessages = messageList.get.response.db();
        const responseMessage = [...responseMessages].reverse().find(carriesToolCall);
        if (responseMessage?.content) {
          const metadata =
            typeof responseMessage.content.metadata === 'object' && responseMessage.content.metadata !== null
              ? (responseMessage.content.metadata as Record<string, any>)
              : {};
          responseMessage.content.metadata = metadata;
          metadata[metadataKey] = metadata[metadataKey] || {};
          metadata[metadataKey][toolCallId] = entry;
          return;
        }

        // A sibling suspension may have already flushed the shared assistant response, leaving
        // this iteration's tool call absent from the response view. Update the drained message
        // and mark it unsaved again so this sibling's metadata reaches the next flush.
        const target = [...messageList.get.all.db()].reverse().find(carriesToolCall);
        if (!target?.content) {
          logger?.warn?.(
            `addToolMetadata could not find an assistant message for tool call ${toolCallId} (${toolName}); ${metadataKey} entry was not persisted.`,
          );
          return;
        }
        const existingMetadata =
          typeof target.content.metadata === 'object' && target.content.metadata !== null
            ? (target.content.metadata as Record<string, any>)
            : {};
        const existingEntries = (existingMetadata[metadataKey] ?? {}) as Record<string, any>;
        const updated = messageList.updateMessageMetadataByToolCallId(toolCallId, {
          [metadataKey]: { ...existingEntries, [toolCallId]: entry },
        });
        if (!updated) {
          // updateMessageMetadataByToolCallId already logged a warning; add the metadata context
          // at debug level instead of duplicating the warn.
          logger?.debug?.(
            `addToolMetadata could not update the assistant message for tool call ${toolCallId} (${toolName}); ${metadataKey} entry was not persisted.`,
          );
        }
      };

      const removeToolMetadata = async (
        target: { toolCallId?: string; toolName: string; runId?: string },
        type: 'suspension' | 'approval',
      ): Promise<Record<string, any> | undefined> => {
        const { saveQueueManager, memoryConfig, threadId } = _internal || {};

        const metadataKey = type === 'suspension' ? 'suspendedTools' : 'pendingToolApprovals';
        const expectedPartType = type === 'suspension' ? 'data-tool-call-suspended' : 'data-tool-call-approval';
        const entryMatches = (entry: any, fallbackToolCallId?: string): boolean => {
          const entryToolCallId = typeof entry?.toolCallId === 'string' ? entry.toolCallId : fallbackToolCallId;
          const entryToolName = entry?.parentToolName ?? entry?.toolName;
          const entryRunId = type === 'approval' ? entry?.delegatedRunId : (entry?.delegatedRunId ?? entry?.runId);
          if (target.toolCallId) return entryToolCallId === target.toolCallId;
          return entryToolName === target.toolName && !!target.runId && entryRunId === target.runId;
        };

        const changedMessages: MastraDBMessage[] = [];
        let matchedEntry: Record<string, any> | undefined;
        for (const message of messageList.get.all.db()) {
          if (message.role !== 'assistant') continue;

          let messageChanged = false;
          const metadata =
            typeof message.content.metadata === 'object' && message.content.metadata !== null
              ? (message.content.metadata as Record<string, any>)
              : undefined;
          const entries = metadata?.[metadataKey] as Record<string, any> | undefined;
          if (entries) {
            for (const [key, entry] of Object.entries(entries)) {
              if (entryMatches(entry, key)) {
                matchedEntry ??= { ...entry, toolCallId: entry?.toolCallId ?? key };
                delete entries[key];
                messageChanged = true;
              }
            }
            if (Object.keys(entries).length === 0) delete metadata![metadataKey];
          }

          message.content.parts = message.content.parts?.map(part => {
            if (part.type !== expectedPartType || !entryMatches(part.data)) return part;
            matchedEntry ??= { ...(part.data as Record<string, any>) };
            if ((part.data as { resumed?: boolean }).resumed) return part;
            messageChanged = true;
            return { ...part, data: { ...(part.data as any), resumed: true } };
          });

          if (messageChanged) changedMessages.push(message);
        }

        if (changedMessages.length === 0 || !saveQueueManager || !threadId) return matchedEntry;
        messageList.add(changedMessages, 'response');
        try {
          await saveQueueManager.flushMessages(messageList, threadId, memoryConfig);
        } catch (error) {
          logger?.error('Error removing tool suspension metadata:', error);
        }
        return matchedEntry;
      };

      // Live counterpart of the persisted `resumed: true` marker. The eventual `tool-result` reuses
      // this toolCallId, so clients need an explicit signal that the suspension/approval was answered.
      // The payload mirrors the cleared entry so the replacement client part keeps the question.
      const emitToolCallResumed = async (entry: Record<string, any>, kind: 'suspension' | 'approval') => {
        const resumedChunk = await transformChunk({
          type: 'tool-call-resumed',
          runId,
          from: ChunkFrom.AGENT,
          payload: {
            // Auto-resume re-calls the tool under a new id; ack the id the client saw suspended.
            toolCallId: entry.toolCallId ?? inputData.toolCallId,
            toolName: entry.toolName ?? inputData.toolName,
            kind,
            args: entry.args,
            ...(kind === 'suspension' ? { suspendPayload: entry.suspendPayload } : {}),
            resumeSchema: entry.resumeSchema,
          },
          ...(entry.metadata ? { metadata: entry.metadata } : {}),
        });
        safeEnqueue(controller, resumedChunk);
      };

      // Helper function to flush messages before suspension
      const flushMessagesBeforeSuspension = async () => {
        const saveQueueManager = readScoped(scopeCtx, SAVE_QUEUE_MANAGER_KEY, 'saveQueueManager');
        const memoryConfig = readScoped(scopeCtx, MEMORY_CONFIG_KEY, 'memoryConfig');
        const threadId = readScoped(scopeCtx, THREAD_ID_KEY, 'threadId');
        const resourceId = readScoped(scopeCtx, RESOURCE_ID_KEY, 'resourceId');
        const memory = readScoped(scopeCtx, MEMORY_KEY, 'memory');

        if (!saveQueueManager || !threadId || memoryConfig?.readOnly) {
          return;
        }

        try {
          // Ensure thread exists before flushing messages
          const threadExists = readScoped(scopeCtx, THREAD_EXISTS_KEY, 'threadExists');
          if (memory && !threadExists && resourceId) {
            const thread = await memory.getThreadById?.({ threadId });
            if (!thread) {
              // Thread doesn't exist yet, create it now
              await memory.createThread?.({
                threadId,
                resourceId,
                memoryConfig,
              });
            }
            writeScoped(scopeCtx, THREAD_EXISTS_KEY, 'threadExists', true);
          }

          // Flush all pending messages immediately
          await saveQueueManager.flushMessages(messageList, threadId, memoryConfig);
        } catch (error) {
          logger?.error('Error flushing messages before suspension:', error);
        }
      };

      // Provider-executed tools are handled entirely by the stream path
      // (tool-call and tool-result chunks in llm-execution-step), so skip client execution.
      if (inputData.providerExecuted) {
        return inputData;
      }

      // Resolve the tool key for activeTools enforcement (may differ from toolName when matched by id)
      const toolKey = stepTools?.[inputData.toolName]
        ? inputData.toolName
        : Object.entries(stepTools || {}).find(([_, t]: [string, any]) => t === tool)?.[0];

      // Reject if tool doesn't exist or isn't in the active set for this step
      const isHiddenByActiveTools = stepActiveTools && toolKey && !stepActiveTools.includes(toolKey);
      if (!tool || isHiddenByActiveTools) {
        const availableToolNames = stepActiveTools ?? Object.keys(stepTools || {});
        const availableToolsStr =
          availableToolNames.length > 0 ? ` Available tools: ${availableToolNames.join(', ')}` : '';
        return {
          // The workflow step output crosses the evented engine's pubsub boundary, where
          // `JSON.stringify` reduces Error instances to `{}`. Serialize to a plain object
          // here so `name`/`message`/`stack` survive and the consumer can reify the Error.
          error: serializeToolError(
            new ToolNotFoundError(
              `Tool "${inputData.toolName}" not found.${availableToolsStr}. Call tools by their exact name only — never add prefixes, namespaces, or colons.`,
            ),
          ),
          ...inputData,
        };
      }

      if (tool && 'onInputAvailable' in tool && !inputAlreadyAnnouncedEagerly) {
        try {
          await tool?.onInputAvailable?.({
            toolCallId: inputData.toolCallId,
            input: inputData.args,
            messages: messageList.get.input.aiV5.model(),
            abortSignal,
          });
        } catch (error) {
          logger?.error('Error calling onInputAvailable', error);
        }
        // Announced before `execute`, so a bailout raised from inside the tool body has
        // already fired the hook. Record it so the foreach's re-run does not fire it
        // again for the same call.
        if (eagerBailout) eagerBailout.inputAvailableCalled = true;
      }

      if (!tool.execute) {
        return inputData;
      }

      try {
        // The factory closure value is authoritative when set: a function-valued policy
        // doesn't survive `RequestContext.toJSON()` across the evented engine's event bus,
        // so reading only from requestContext would lose it. Fall back to requestContext for
        // direct callers (e.g. legacy tests) that seed the value there.
        const requireToolApproval =
          requireToolApprovalFromFactory ?? requestContext.get('__mastra_requireToolApproval');

        let resumeDataFromArgs: any = undefined;
        let args: any = inputData.args;

        if (typeof inputData.args === 'object' && inputData.args !== null) {
          const { resumeData: resumeDataFromInput, ...argsFromInput } = inputData.args;
          args = argsFromInput;
          resumeDataFromArgs = resumeDataFromInput;
        }

        const resumeData = resumeDataFromArgs ?? workflowResumeData;

        // Match the nullish fallback above: null/undefined use framework identity, while other falsy values are valid model payloads.
        const isResumeToolCall = resumeDataFromArgs != null;

        // Reuse the called-strategy scheduling verdict when available so approval policies
        // are evaluated exactly once per call. Other paths retain execution-time evaluation.
        const approvalVerdicts = readScoped(scopeCtx, TOOL_APPROVAL_VERDICTS_KEY, 'toolApprovalVerdicts');
        const cachedApprovalVerdict = approvalVerdicts?.get(inputData.toolCallId);
        approvalVerdicts?.delete(inputData.toolCallId);
        const toolRequiresApproval =
          cachedApprovalVerdict ??
          (await resolveToolApprovalVerdict({
            tool,
            requireToolApproval,
            context: buildToolApprovalContext({
              toolName: inputData.toolName,
              args,
              requestContext,
              workspace: readScoped(scopeCtx, STEP_WORKSPACE_KEY, 'stepWorkspace'),
            }),
            logger,
          }));

        // On resume, the live `requireToolApproval` policy may be gone: function-form
        // policies do not survive RequestContext serialization, and decline/approve
        // helpers typically only pass `{ runId, toolCallId }` — not the original option.
        // The suspend payload still records that this step waited for approval, so treat
        // that as authoritative for the resume decision (especially declines).
        //
        // Nested sub-agent/workflow approvals also write `requireToolApproval` on the
        // outer suspend payload, but they additionally set `suspendedToolRunId`. Those
        // must resume into the nested tool path — not the outer approval short-circuit —
        // even when a live outer `requireToolApproval` policy is still present.
        const isDelegatedApproval = Boolean(
          suspendData &&
          typeof suspendData === 'object' &&
          (suspendData as { suspendedToolRunId?: unknown }).suspendedToolRunId,
        );
        const suspendedForApproval = Boolean(
          suspendData &&
          typeof suspendData === 'object' &&
          (suspendData as { requireToolApproval?: unknown }).requireToolApproval &&
          !isDelegatedApproval,
        );
        const approvalDecision =
          workflowResumeData != null &&
          typeof workflowResumeData === 'object' &&
          typeof (workflowResumeData as Record<string, unknown>).approved === 'boolean'
            ? (workflowResumeData as { approved: boolean; reason?: string })
            : undefined;
        // Gate a fresh call or a prior outer approval suspend. Once an approved tool
        // suspends during execution for its own resume data, do not require approval again.
        // Approval decisions must come from the workflow resume boundary; model-authored
        // resumeData is untrusted and cannot grant or decline consent.
        const approvalGated =
          !isDelegatedApproval && (suspendedForApproval || (toolRequiresApproval && suspendData === undefined));

        // Schema for tool call approval - used for both streaming and metadata
        const approvalSchema = toStandardSchema(
          z.object({
            approved: z
              .boolean()
              .describe(
                'Controls if the tool call is approved or not, should be true when approved and false when declined',
              ),
            reason: z
              .string()
              .optional()
              .describe('Optional explanation for the decision, surfaced to the model when the tool call is declined'),
          }),
        );

        // The real suspension sequence, extracted so it has exactly one implementation with
        // two entry points: the tool's own `suspend()` closure below, and the hand-back of an
        // eager attempt that suspended at runtime. Defined here because it closes over
        // `args`, `transformChunk`, `flushMessagesBeforeSuspension` and `approvalSchema`, and
        // called before approval gating so a handed-back call is never re-gated or re-run.
        const raiseToolSuspension = async (suspendPayload: any, options?: SuspendOptions): Promise<any> => {
          if (options?.requireToolApproval) {
            const innerApproval =
              typeof options.requireToolApproval === 'object' && options.requireToolApproval
                ? options.requireToolApproval
                : typeof suspendPayload?.requireToolApproval === 'object' && suspendPayload?.requireToolApproval
                  ? suspendPayload.requireToolApproval
                  : null;

            const approvalToolName = innerApproval?.toolName ?? inputData.toolName;
            const approvalArgs = innerApproval?.args !== undefined ? innerApproval.args : inputData.args;

            await stopGoalActivity({
              agentId,
              runId,
              now: readScoped(scopeCtx, NOW_KEY, 'now'),
            });
            const approvalChunk = await transformChunk({
              type: 'tool-call-approval',
              runId,
              from: ChunkFrom.AGENT,
              payload: {
                toolCallId: inputData.toolCallId,
                toolName: approvalToolName,
                args: approvalArgs,
                resumeSchema: JSON.stringify(standardSchemaToJSONSchema(approvalSchema)),
                updatedAt: Date.now(),
              },
            });
            if (outputWriter) {
              await outputWriter(approvalChunk);
            } else {
              safeEnqueue(controller, approvalChunk);
            }

            // Add approval metadata to message before persisting
            addToolMetadata({
              toolCallId: inputData.toolCallId,
              toolName: approvalToolName,
              args: approvalArgs,
              ...(approvalToolName !== inputData.toolName || approvalArgs !== inputData.args
                ? { parentToolName: inputData.toolName, parentArgs: inputData.args }
                : {}),
              type: 'approval',
              suspendedToolRunId: options.runId,
              resumeSchema: JSON.stringify(
                standardSchemaToJSONSchema(
                  toStandardSchema(
                    z.object({
                      approved: z
                        .boolean()
                        .describe(
                          'Controls if the tool call is approved or not, should be true when approved and false when declined',
                        ),
                    }),
                  ),
                ),
              ),
              metadata: approvalChunk.metadata,
            });

            // Flush messages before suspension to ensure they are persisted
            await flushMessagesBeforeSuspension();

            return suspend(
              {
                type: 'approval',
                requireToolApproval: {
                  toolCallId: inputData.toolCallId,
                  toolName: approvalToolName,
                  args: approvalArgs,
                },
                __streamState: streamState.serialize(),
                __agentId: agentId,
                ...(agentVersionId ? { __agentVersionId: agentVersionId } : {}),
                // Persist the inner suspended run id in the workflow snapshot, partitioned per
                // tool call (resumeLabel = toolCallId). Persisted message metadata exposes the
                // same id as delegatedRunId for cold reloads, while the snapshot remains the
                // runtime source for routing this targeted resume.
                suspendedToolRunId: options.runId,
              },
              {
                resumeLabel: inputData.toolCallId,
              },
            );
          } else {
            const suspensionChunk = await transformChunk({
              type: 'tool-call-suspended',
              runId,
              from: ChunkFrom.AGENT,
              payload: {
                toolCallId: inputData.toolCallId,
                toolName: inputData.toolName,
                suspendPayload,
                args: inputData.args,
                resumeSchema: options?.resumeSchema,
              },
            });
            safeEnqueue(controller, suspensionChunk);

            // Add suspension metadata to message before persisting
            addToolMetadata({
              toolCallId: inputData.toolCallId,
              toolName: inputData.toolName,
              args,
              suspendPayload,
              suspendedToolRunId: options?.runId,
              type: 'suspension',
              resumeSchema: options?.resumeSchema,
              metadata: suspensionChunk.metadata,
            });

            // Flush messages before suspension to ensure they are persisted
            await flushMessagesBeforeSuspension();

            return await suspend(
              {
                toolCallSuspended: suspendPayload,
                __streamState: streamState.serialize(),
                __agentId: agentId,
                ...(agentVersionId ? { __agentVersionId: agentVersionId } : {}),
                toolCallId: inputData.toolCallId,
                toolName: inputData.toolName,
                resumeLabel: options?.resumeLabel,
                suspendedToolRunId: options?.runId,
              },
              {
                resumeLabel: inputData.toolCallId,
              },
            );
          }
        };

        // An eager attempt that suspended at runtime hands its intent back here. The body
        // already ran once on that attempt, so raise the real suspension from this — the
        // owning foreach iteration — instead of running the tool again. The intent wins over
        // any value or error the tool produced after swallowing the bailout.
        // This deliberately skips `approvalGated` below: an approval-requiring tool is never
        // dispatched eagerly (the eligibility check excludes every approval source), so there is
        // no approval decision to re-make here. A runtime `suspend({ requireToolApproval })` is
        // still honoured — the intent's options carry it into the approval branch of the helper.
        if (eagerSuspensionIntent) {
          return await raiseToolSuspension(eagerSuspensionIntent.suspendPayload, eagerSuspensionIntent.options);
        }

        if (approvalGated) {
          if (!approvalDecision) {
            await stopGoalActivity({
              agentId,
              runId,
              now: readScoped(scopeCtx, NOW_KEY, 'now'),
            });
            const approvalChunk = await transformChunk({
              type: 'tool-call-approval',
              runId,
              from: ChunkFrom.AGENT,
              payload: {
                toolCallId: inputData.toolCallId,
                toolName: inputData.toolName,
                args: inputData.args,
                resumeSchema: JSON.stringify(standardSchemaToJSONSchema(approvalSchema)),
                updatedAt: Date.now(),
              },
            });
            if (outputWriter) {
              await outputWriter(approvalChunk);
            } else {
              safeEnqueue(controller, approvalChunk);
            }

            // Add approval metadata to message before persisting
            addToolMetadata({
              toolCallId: inputData.toolCallId,
              toolName: inputData.toolName,
              args: inputData.args,
              type: 'approval',
              resumeSchema: JSON.stringify(standardSchemaToJSONSchema(approvalSchema)),
              metadata: approvalChunk.metadata,
            });

            // Flush messages before suspension to ensure they are persisted
            await flushMessagesBeforeSuspension();

            return suspend(
              {
                requireToolApproval: {
                  toolCallId: inputData.toolCallId,
                  toolName: inputData.toolName,
                  args: inputData.args,
                },
                __streamState: streamState.serialize(),
                __agentId: agentId,
                ...(agentVersionId ? { __agentVersionId: agentVersionId } : {}),
              },
              {
                resumeLabel: inputData.toolCallId,
              },
            );
          } else {
            // Remove approval metadata since we're resuming (either approved or declined)
            const clearedApproval = await removeToolMetadata(
              { toolCallId: inputData.toolCallId, toolName: inputData.toolName },
              'approval',
            );
            if (clearedApproval) await emitToolCallResumed(clearedApproval, 'approval');

            if (!approvalDecision.approved) {
              // Return the approval decision (not a `result` string) so it persists as
              // `state: 'output-denied'` with `approval`. The denial reason carries the
              // caller-supplied reason when one was provided, otherwise the default string
              // so downstream consumers/UI keep the same message.
              return {
                approval: {
                  id: inputData.toolCallId,
                  approved: false,
                  reason: resolveDeclineReason(approvalDecision),
                },
                ...inputData,
              };
            }
          }
        }

        // When an approval-gated tool is approved on resume, tag the resolved output with the
        // approval decision so it round-trips through persistence as `approval: { approved: true }`.
        // Use `approvalGated` (not only the live policy) so approve-after-policy-loss still tags.
        const approvalGrant =
          approvalGated && approvalDecision?.approved === true
            ? ({ approval: { id: inputData.toolCallId, approved: true as const } } as const)
            : undefined;

        //this is to avoid passing resume data to the tool if it's not needed
        // For agent tools, always pass resume data so the agent tool wrapper knows to call
        // resumeStream instead of stream (otherwise the sub-agent restarts from scratch)
        const isAgentTool = inputData.toolName?.startsWith('agent-');
        const isWorkflowTool = inputData.toolName?.startsWith('workflow-');
        const resumeDataToPassToToolOptions =
          !isAgentTool &&
          approvalGated &&
          resumeData &&
          Object.keys(resumeData).length === 1 &&
          'approved' in resumeData
            ? undefined
            : resumeData;

        const toolOptions: MastraToolInvocationOptions = {
          abortSignal,
          toolCallId: inputData.toolCallId,
          // Agent tools receive the exact processor-adjusted prompt visible to the parent model.
          // Regular tools retain the input-only context expected by the AI SDK tool contract.
          messages: isAgentTool
            ? (readScoped(scopeCtx, STEP_MODEL_MESSAGES_KEY, 'stepModelMessages') ?? messageList.get.all.aiV5.model())
            : messageList.get.input.aiV5.model(),
          getMessages: () => messageList.get.all.db(),
          outputWriter,
          // Pass current step span as parent for tool call spans
          tracingContext: modelSpanTracker?.getTracingContext(),
          // Pass workspace from the run scope (set by llmExecutionStep via prepareStep/processInputStep)
          workspace: readScoped(scopeCtx, STEP_WORKSPACE_KEY, 'stepWorkspace'),
          // Forward requestContext so tools receive values set by the workflow step
          requestContext,
          actor,
          mcp,
          // Let tools that read thread history mid-stream (e.g. forked subagents
          // cloning the parent thread) drain the save queue so the store reflects
          // the latest user/assistant messages before they read.
          flushMessages: (() => {
            const sqm = readScoped(scopeCtx, SAVE_QUEUE_MANAGER_KEY, 'saveQueueManager');
            const tid = readScoped(scopeCtx, THREAD_ID_KEY, 'threadId');
            const mcfg = readScoped(scopeCtx, MEMORY_CONFIG_KEY, 'memoryConfig');
            return sqm && tid ? () => sqm.flushMessages(messageList, tid, mcfg) : undefined;
          })(),
          suspend: async (suspendPayload: any, options?: SuspendOptions) => {
            // A tool can suspend at runtime without declaring a suspend schema, so the
            // eager eligibility whitelist cannot see it coming. Bail here, before any
            // suspension side effect (chunk, metadata, flush) has happened, so the call
            // is genuinely handed back to the ordinary foreach rather than half-suspended
            // on this path. Bailing after the chunk was emitted would leave a suspension
            // announced that never suspends.
            if (isEagerExecution) {
              // Recorded before the throw, because the throw is not enough on its own: it
              // unwinds through the tool's body, and a tool that catches it would otherwise
              // return normally and have that return adopted as the call's result.
              const reason = `"${inputData.toolName}" requested suspension`;
              // What the tool asked for rides out with the bailout, so the owning foreach
              // iteration can raise the real suspension instead of running the body again.
              // An explicit pick, not the whole `SuspendOptions`, which is unbounded.
              const suspension: EagerSuspensionIntent = {
                suspendPayload,
                options: {
                  resumeLabel: options?.resumeLabel,
                  resumeSchema: options?.resumeSchema,
                  runId: options?.runId,
                  requireToolApproval: options?.requireToolApproval,
                },
              };
              if (eagerBailout) {
                eagerBailout.reason = reason;
                eagerBailout.suspension = suspension;
              }
              throw new EagerToolExecutionNotRun(reason, {
                inputAvailableCalled: eagerBailout?.inputAvailableCalled,
                suspension,
              });
            }
            return await raiseToolSuspension(suspendPayload, options);
          },
          resumeData: resumeDataToPassToToolOptions,
          // The payload this tool call suspended with (see `toolCallSuspended` above), so a
          // resumed tool can continue from its own state instead of re-deriving it.
          ...(resumeDataToPassToToolOptions != null &&
          suspendData &&
          typeof suspendData === 'object' &&
          'toolCallSuspended' in suspendData
            ? { suspendPayload: (suspendData as { toolCallSuspended?: unknown }).toolCallSuspended }
            : {}),
        };

        const modelSuppliedSuspendedToolCallId = args?.suspendedToolCallId;
        const modelSuppliedSuspendedToolRunId = args?.suspendedToolRunId;
        if (args && typeof args === 'object') {
          delete args.suspendedToolCallId;
          delete args.suspendedToolRunId;
        }

        // Delegated identity is trusted only after it is tied to framework-persisted
        // suspension state. Nullish, not truthy: false / 0 / '' are valid resume payloads.
        const needsRunIdLookup = resumeDataToPassToToolOptions != null && (isAgentTool || isWorkflowTool);
        let resolvedSuspensionIdentity: ResolvedSuspendedToolIdentity | undefined;
        if (needsRunIdLookup) {
          resolvedSuspensionIdentity = resolveFrameworkSuspendedToolIdentity({
            toolCallId: inputData.toolCallId,
            toolName: inputData.toolName,
            resumeSource: isResumeToolCall ? 'model' : 'framework',
            modelSuppliedSuspendedToolCallId: isResumeToolCall ? modelSuppliedSuspendedToolCallId : undefined,
            modelSuppliedSuspendedToolRunId: isResumeToolCall ? modelSuppliedSuspendedToolRunId : undefined,
            suspendData,
            messages: messageList.get.all.db(),
          });

          if (resolvedSuspensionIdentity) {
            // Agentic execution disables input validation, so a resumed call can carry
            // `args: null`; guard the mutation so the invalid-arguments check below still runs.
            if (args && typeof args === 'object') {
              args.suspendedToolRunId = resolvedSuspensionIdentity.runId;
            }
            toolOptions.suspendedToolRunId = resolvedSuspensionIdentity.runId;
          }
        }

        // Clear the suspension entry for BOTH resume conventions: `resumeData` embedded in the
        // LLM's re-emitted args (autoResumeSuspendedTools) and the workflow-level resumeData that
        // `agent.resumeStream(resumeData, { runId, toolCallId })` delivers. `isResumeToolCall` only
        // covers the former — it stays args-specific because the runId lookup above depends on
        // that narrower meaning.
        // Nullish, not truthy: `false` / `0` / `''` are valid resume payloads for a tool whose
        // resumeSchema is a primitive (e.g. a boolean decline), and they must clear the entry too.
        //
        // Keyed on `approvalGated`, not the live `toolRequiresApproval`, for the same reason as
        // `approvalGrant` above: on an approve-after-policy-loss resume the live policy is gone
        // while the suspension was an approval one, which cleans up its own metadata in the
        // branch above. Using the live policy here would run the generic suspension cleanup on
        // top of it, and `removeToolMetadata`'s toolCallId -> toolName fallback could then drop a
        // concurrently suspended sibling that shares this tool name.
        if (!approvalGated && resumeData != null) {
          const cleanupTarget = needsRunIdLookup
            ? resolvedSuspensionIdentity
            : { toolCallId: inputData.toolCallId, toolName: inputData.toolName };
          const clearedEntry = cleanupTarget
            ? await removeToolMetadata(cleanupTarget, resolvedSuspensionIdentity?.type ?? 'suspension')
            : undefined;
          // Model-supplied `resumeData` alone is not proof of a suspension; only ack a cleared entry.
          if (clearedEntry) {
            await emitToolCallResumed(clearedEntry, resolvedSuspensionIdentity?.type ?? 'suspension');
          }
        }

        if (args === null || args === undefined) {
          return {
            error: serializeToolError(
              new Error(
                `Tool "${inputData.toolName}" received invalid arguments — the provided JSON could not be parsed. Please provide valid JSON arguments.`,
              ),
            ),
            ...inputData,
          };
        }

        if (isAgentTool) {
          if (typeof args === 'object' && args !== null && 'prompt' in args) {
            args.threadId = readScoped(scopeCtx, THREAD_ID_KEY, 'threadId');
            args.resourceId = readScoped(scopeCtx, RESOURCE_ID_KEY, 'resourceId');
          }
        }

        // Tool-level FGA (TOOLS_EXECUTE) is enforced inside the tool wrapper
        // (`createExecute` in tools/tool-builder/builder.ts), which runs on every
        // execution path — inline, background dispatch, and durable steps — using
        // the canonical resource id (`<agentId>:<toolName>`, the MCP id, or the
        // standalone name). Checking here as well would authorize a bare,
        // non-canonical id that the durable path never checks, so it is not
        // duplicated (keeps regular and durable authorization identical).

        const llmBgOverrides =
          typeof args === 'object' && args !== null && '_background' in args ? args._background : undefined;

        if (llmBgOverrides) {
          delete args._background;
        }

        // --- Background task dispatch ---
        const agentBgConfig = readScoped(scopeCtx, AGENT_BACKGROUND_CONFIG_KEY, 'agentBackgroundConfig');
        // Settled by `onResult` once the authoritative background result has
        // been reconciled into the message list (or reconciliation threw).
        // Lives outside `taskContext` because the `awaited` disposition below
        // must block the turn on it after the ladder returns.
        let resolveReconciliation!: (outcome: { error?: unknown }) => void;
        const reconciliationComplete = new Promise<{ error?: unknown }>(resolve => {
          resolveReconciliation = resolve;
        });
        const backgroundResultMetadata = (taskId: string, status: 'running' | 'completed' | 'failed') => ({
          ...inputData.providerMetadata,
          mastra: {
            ...inputData.providerMetadata?.mastra,
            backgroundTask: { taskId, status },
          },
        });
        const bgOutcome = await dispatchBackgroundTool({
          // The in-process engine's released contract: no
          // checkIfRunning probe (dispatch here is only re-entered by caller
          // action, never redelivered), and a dispatch failure propagates as
          // a tool error instead of silently degrading to sync execution.
          existingRunningTask: 'dispatch-duplicate',
          dispatchFailure: 'propagate',
          backgroundTaskManager: readScoped(scopeCtx, BACKGROUND_TASK_MANAGER_KEY, 'backgroundTaskManager'),
          agentBackgroundConfig: agentBgConfig,
          managerConfig: readScoped(scopeCtx, BACKGROUND_TASK_MANAGER_CONFIG_KEY, 'backgroundTaskManagerConfig'),
          toolBackgroundConfig: (tool as any).backgroundConfig as ToolBackgroundConfig | undefined,
          llmBgOverrides,
          args,
          toolName: inputData.toolName,
          toolCallId: inputData.toolCallId,
          agentId,
          threadId: readScoped(scopeCtx, THREAD_ID_KEY, 'threadId'),
          resourceId: readScoped(scopeCtx, RESOURCE_ID_KEY, 'resourceId'),
          runId,
          resumeData: resumeDataToPassToToolOptions,
          logger,
          emitTaskStarted: async task => {
            // Emit background-task-started chunk. Use safeEnqueue: the
            // agent stream may have closed by the time this fires (e.g.
            // when the controller closes mid-dispatch in a long-lived
            // streamUntilIdle wrapper) — without the guard, the throw
            // bubbles up through the AI-SDK-v5 tool builder and gets
            // wrapped as `TOOL_EXECUTION_FAILED: Invalid state:
            // Controller is already closed`.
            const backgroundTaskStartedChunk = {
              type: 'background-task-started' as const,
              runId,
              from: ChunkFrom.AGENT,
              payload: {
                taskId: task.id,
                toolName: inputData.toolName,
                toolCallId: inputData.toolCallId,
              },
            };
            safeEnqueue(controller, backgroundTaskStartedChunk);
            try {
              await options?.onChunk?.(backgroundTaskStartedChunk);
            } catch (error) {
              logger?.warn?.('Error invoking onChunk for background-task-started', {
                toolCallId: inputData.toolCallId,
                toolName: inputData.toolName,
                error,
                errorMessage: error instanceof Error ? error.message : undefined,
                errorStack: error instanceof Error ? error.stack : undefined,
              });
            }
          },
          taskContext: info => {
            const toolBgConfig = (tool as any).backgroundConfig as ToolBackgroundConfig | undefined;
            // Resolve the tool executor from the current closure
            const stepTools = (readScoped(scopeCtx, STEP_TOOLS_KEY, 'stepTools') as Tools | undefined) || tools;
            const resolvedTool =
              stepTools?.[inputData.toolName] ||
              Object.values(stepTools || {})?.find((t: any) => 'id' in t && t.id === inputData.toolName);
            if (!resolvedTool?.execute) {
              throw new ToolNotFoundError(inputData.toolName);
            }
            let backgroundChunkTransformQueue: Promise<void> = Promise.resolve();
            const emittedReplayedToolCalls = new Set<string>();

            return {
              // Executor — uses the tool from the current closure
              executor: {
                execute: async (
                  bgArgs: Record<string, unknown>,
                  opts?: {
                    abortSignal?: AbortSignal;
                    onProgress?: (chunk: BackgroundTaskProgressChunk) => Promise<void>;
                    suspend?: (data?: unknown, options?: SuspendOptions) => Promise<void>;
                    resumeData?: unknown;
                    suspendedToolRunId?: string;
                  },
                ) => {
                  // Override the agent loop's `suspend`/`resumeData` (which
                  // would suspend the AGENT run via tool-call-approval) with
                  // the bg-task workflow's, so calling `suspend()` from the
                  // tool pauses the bg-task run instead.
                  const taskId = info.getTaskId()!;
                  const execution = await executeAdoptedBackgroundOperation({
                    taskId,
                    disposition: info.disposition === 'awaited' ? 'awaited' : 'deferred',
                    abortSignal: opts?.abortSignal ?? options?.abortSignal,
                    execute: background =>
                      resolvedTool.execute!(bgArgs, {
                        ...toolOptions,
                        isBackgroundTask: true,
                        background,
                        [BACKGROUND_WORK_CONTEXT]: {
                          originRunId: runId,
                          originToolCallId: inputData.toolCallId,
                          taskId,
                          invocationKind: isAgentTool ? 'agent' : 'tool',
                          disposition: info.disposition === 'awaited' ? 'awaited' : 'deferred',
                        },
                        ...(opts?.resumeData !== undefined ? { resumeData: opts.resumeData } : {}),
                        // Framework-resolved delegated run id recovered from persisted
                        // suspension state (#23739) — never the model-authored one.
                        suspendedToolRunId: opts?.suspendedToolRunId,
                        suspend: async (data?: unknown, options?: SuspendOptions) => {
                          await toolOptions.suspend?.(data, options);
                          return opts?.suspend?.(data, options);
                        },
                        outputWriter: async (chunk: any) => {
                          await opts?.onProgress?.(chunk);
                          return toolOptions.outputWriter?.(chunk);
                        },
                        abortSignal: opts?.abortSignal ?? options?.abortSignal,
                      } as any),
                    onCancelError: error => logger?.warn('Failed to cancel adopted background operation', error),
                  });

                  let rawResult = execution.result;
                  if (execution.adopted) {
                    const outputValidation = validateToolOutput(
                      resolveToolOutputValidationSchema(resolvedTool),
                      rawResult,
                      inputData.toolName,
                      false,
                    );
                    rawResult = outputValidation.error ?? outputValidation.data;
                  }
                  const result = ensureSerializable(rawResult);

                  if ('onOutput' in resolvedTool && typeof (resolvedTool as any).onOutput === 'function') {
                    try {
                      await (resolvedTool as any).onOutput({
                        toolCallId: inputData.toolCallId,
                        toolName: inputData.toolName,
                        output: result,
                        abortSignal: opts?.abortSignal,
                      });
                    } catch (error) {
                      logger?.error('Error calling onOutput', error);
                    }
                  }

                  return result;
                },
              },

              // Synthetic tool-call/tool-result emitter. Bg-task lifecycle
              // chunks (running/output/completed/failed/cancelled) are NOT
              // re-emitted here — `bgManager.stream(...)` is the single
              // source of truth for those. We only emit the synthetic
              // tool-call (at dispatch time) and tool-result / tool-error
              // chunks so UIs rendering this stream can show the tool's
              // outcome inline with the conversation.
              onChunk: chunk => {
                backgroundChunkTransformQueue = backgroundChunkTransformQueue
                  .then(async () => {
                    const bgRunId = chunk.payload.runId;
                    const replayKey = `${bgRunId}:${chunk.payload.toolCallId}`;
                    if (
                      (bgRunId !== runId || (bgRunId === runId && workflowResumeData != null)) &&
                      !emittedReplayedToolCalls.has(replayKey)
                    ) {
                      safeEnqueue(
                        controller,
                        await transformChunk({
                          type: 'tool-call',
                          runId: bgRunId,
                          from: ChunkFrom.AGENT,
                          payload: {
                            toolCallId: chunk.payload.toolCallId,
                            toolName: chunk.payload.toolName,
                            args: inputData.args,
                            providerMetadata: inputData.providerMetadata as ProviderMetadata | undefined,
                            providerExecuted: inputData.providerExecuted,
                            title: getToolTitle(resolvedTool),
                          },
                        }),
                      );
                      emittedReplayedToolCalls.add(replayKey);
                    }

                    if (chunk.type === 'background-task-completed') {
                      safeEnqueue(
                        controller,
                        await transformChunk({
                          type: 'tool-result',
                          runId: bgRunId,
                          from: ChunkFrom.AGENT,
                          payload: {
                            toolCallId: chunk.payload.toolCallId,
                            toolName: chunk.payload.toolName,
                            args: inputData.args,
                            result: chunk.payload.result,
                            providerMetadata: backgroundResultMetadata(chunk.payload.taskId, 'completed'),
                            providerExecuted: inputData.providerExecuted,
                          },
                        }),
                      );
                    } else if (chunk.type === 'background-task-failed') {
                      safeEnqueue(
                        controller,
                        await transformChunk({
                          type: 'tool-error',
                          runId: bgRunId,
                          from: ChunkFrom.AGENT,
                          payload: {
                            toolCallId: chunk.payload.toolCallId,
                            toolName: chunk.payload.toolName,
                            error: chunk.payload.error,
                            args: inputData.args,
                            providerMetadata: backgroundResultMetadata(chunk.payload.taskId, 'failed'),
                            providerExecuted: inputData.providerExecuted,
                          },
                        }),
                      );
                    }
                  })
                  .catch(error => {
                    logger?.warn?.('Error transforming background task stream chunk', {
                      toolCallId: chunk.payload.toolCallId,
                      toolName: chunk.payload.toolName,
                      runId: chunk.payload.runId,
                      error,
                      errorMessage: error instanceof Error ? error.message : undefined,
                      errorStack: error instanceof Error ? error.stack : undefined,
                    });
                  });
              },

              // Result injector — updates the existing tool-invocation in the
              // message list (keyed by toolCallId) with the real result, then
              // flushes to memory. This matters because the initial turn
              // persisted a placeholder ("Background task started...") as the
              // tool-result for the same toolCallId; appending a second
              // tool-result would leave two conflicting entries in memory and
              // the LLM on the next turn would re-dispatch the tool thinking
              // the research was still running.
              onResult: async params => {
                try {
                  await applyBackgroundToolResult({
                    params,
                    currentRunId: runId,
                    hasResumeData: workflowResumeData != null,
                    args,
                    messageList,
                    approvalGrant: approvalGrant as Record<string, unknown> | undefined,
                    baseProviderMetadata: inputData.providerMetadata as ProviderMetadata | undefined,
                    transformForTranscript: async result => {
                      const transformCarrier = await applyToolPayloadTransformToChunk(
                        {
                          type: params.status === 'failed' ? 'tool-error' : 'tool-result',
                          payload: {
                            toolCallId: params.toolCallId,
                            toolName: params.toolName,
                            args,
                            ...(params.status === 'failed' ? { error: params.error } : { result: params.result }),
                          },
                          metadata: {} as Record<string, any>,
                        },
                        {
                          policy: transformSource.policy,
                          toolTransform: transformSource.toolTransform,
                          logger,
                          transformInput: {
                            providerMetadata: inputData.providerMetadata as Record<string, unknown> | undefined,
                          },
                        },
                      );
                      const transcriptArgsTransform = getTransformedToolPayload(
                        transformCarrier.metadata,
                        'transcript',
                        'input-available',
                      );
                      const transcriptResultTransform = getTransformedToolPayload(
                        transformCarrier.metadata,
                        'transcript',
                        params.status === 'failed' ? 'error' : 'output-available',
                      );
                      return {
                        transcriptArgs: hasTransformedToolPayload(transcriptArgsTransform)
                          ? transcriptArgsTransform.transformed
                          : args,
                        transcriptResult: hasTransformedToolPayload(transcriptResultTransform)
                          ? transcriptResultTransform.transformed
                          : result,
                        providerMetadata: withToolPayloadTransformProviderMetadata(
                          inputData.providerMetadata as ProviderMetadata | undefined,
                          transformCarrier.metadata,
                        ) as ProviderMetadata | undefined,
                      };
                    },
                    toModelOutput: (resolvedTool as { toModelOutput?: (output: unknown) => unknown } | undefined)
                      ?.toModelOutput,
                    generateId: readScoped(scopeCtx, GENERATE_ID_KEY, 'generateId'),
                    logger,
                    flush: async () => {
                      const sqm = readScoped(scopeCtx, SAVE_QUEUE_MANAGER_KEY, 'saveQueueManager');
                      const tid = readScoped(scopeCtx, THREAD_ID_KEY, 'threadId');
                      const mcfg = readScoped(scopeCtx, MEMORY_CONFIG_KEY, 'memoryConfig');
                      // readOnly runs must not persist the patched background result
                      // to memory — mirrors the durable engine's readOnly flush guard.
                      if (sqm && tid && !mcfg?.readOnly) {
                        await sqm.flushMessages(messageList, tid, mcfg);
                      }
                    },
                  });

                  resolveReconciliation({});
                  void notifyBackgroundWorkTerminal(mastra, {
                    originRunId: runId,
                    originToolCallId: params.toolCallId,
                    ...(params.runId !== runId ? { executorRunId: params.runId } : {}),
                    taskId: params.taskId,
                    invocationKind: isAgentTool ? 'agent' : 'tool',
                    disposition: info.disposition === 'awaited' ? 'awaited' : 'deferred',
                    status: params.status === 'failed' ? 'failed' : 'completed',
                  });
                } catch (error) {
                  resolveReconciliation({ error });
                  throw error;
                }
              },
              // Execution injector — records background task lifecycle metadata on the
              // assistant message without changing the model-visible tool result.
              onExecution: async params => {
                messageList.updateMessageMetadataByToolCallId(params.toolCallId, {
                  mode: 'stream',
                  backgroundTasks: {
                    [params.toolCallId]: {
                      startedAt: params.startedAt,
                      suspendedAt: params.suspendedAt,
                      taskId: params.taskId,
                    },
                  },
                });
              },

              // Per-task callbacks
              onComplete: toolBgConfig?.onComplete ?? agentBgConfig?.onTaskComplete,
              onFailed: toolBgConfig?.onFailed ?? agentBgConfig?.onTaskFailed,
            };
          },
        });

        if (bgOutcome.status !== 'sync') {
          // `awaited` disposition: block the turn on the authoritative
          // background result instead of returning the placeholder. The
          // reconciliation promise gate ensures `onResult` has patched the
          // message list before the result is returned to the model.
          // Restarted tasks are included (main only had started/resumed —
          // restart-reattach was added during the shared-core extraction):
          // a replayed awaited call still owes the model the real result.
          if (bgOutcome.disposition === 'awaited') {
            const completedTask = await bgOutcome.waitForCompletion({ abortSignal });
            // Cancellation deregisters the task context without calling onResult, so there is no reconciliation to await.
            if (completedTask.status !== 'cancelled') {
              const reconciliation = await reconciliationComplete;
              if (reconciliation.error) {
                throw reconciliation.error;
              }
            }

            if (completedTask.status !== 'completed') {
              throw new Error(
                completedTask.error?.message ??
                  `Background task ${completedTask.status.replace('_', ' ')}: ${completedTask.id}`,
              );
            }

            return {
              result: ensureSerializable(completedTask.result),
              ...inputData,
              providerMetadata: backgroundResultMetadata(bgOutcome.taskId, 'completed'),
              ...(approvalGrant ?? {}),
            };
          }
          if (bgOutcome.status === 'started') {
            // Return placeholder result so the LLM can continue
            return {
              result: bgOutcome.placeholder,
              ...inputData,
              providerMetadata: backgroundResultMetadata(bgOutcome.taskId, 'running'),
              ...(approvalGrant ?? {}),
            };
          }
          return {
            result: bgOutcome.placeholder,
            ...inputData,
            providerMetadata: backgroundResultMetadata(bgOutcome.taskId, 'running'),
          };
        }

        const outcome = await executeToolCall({
          tool: tool as any,
          args,
          toolOptions,
          toolCallId: inputData.toolCallId,
          toolName: inputData.toolName,
          abortSignal,
          // The tool asked to suspend or bail and then swallowed the throw. Its return value
          // is the return value of a call that was never supposed to complete here, so bail
          // before it is published: `onOutput` is a side effect the foreach will produce
          // again when it runs the call for real. "The eager attempt must not run this" is
          // control flow, not a tool failure: turning it into a resolved `{ error }` would
          // defeat the fail-safe, since adoption awaits the eager promise and would record
          // that error as the tool's result instead of running the call normally.
          assertNotBailedOut: error => {
            if (error !== undefined && eagerToolCallDidNotExecute(error)) {
              throw error;
            }
            if (eagerBailout?.reason) {
              throw new EagerToolExecutionNotRun(eagerBailout.reason, {
                inputAvailableCalled: eagerBailout.inputAvailableCalled,
                // A swallowed suspend produces its rejection here rather than in the closure,
                // so the intent has to be re-attached or the hand-back loses it.
                suspension: eagerBailout.suspension,
              });
            }
          },
          logger,
        });
        if (outcome.status === 'aborted') {
          return { aborted: true, ...inputData };
        }
        if (outcome.status === 'error') {
          return { error: serializeToolError(outcome.error), ...inputData };
        }

        return { result: outcome.result, ...inputData, ...(approvalGrant ?? {}) };
      } catch (error) {
        // Re-throw FGA authorization errors instead of swallowing them
        if (error instanceof Error && error.name === 'FGADeniedError') {
          throw error;
        }
        // "The eager attempt must not run this" is control flow, not a tool failure.
        // Turning it into a resolved `{ error }` would defeat the fail-safe: adoption
        // awaits the eager promise and would record that error as the tool's result
        // instead of running the call normally.
        if (eagerToolCallDidNotExecute(error)) {
          throw error;
        }
        // The tool caught the bailout and threw something else without a `cause`: the
        // call still bailed, so hand back the recorded intent instead of a tool failure.
        if (eagerBailout?.reason) {
          throw new EagerToolExecutionNotRun(eagerBailout.reason, {
            inputAvailableCalled: eagerBailout.inputAvailableCalled,
            suspension: eagerBailout.suspension,
          });
        }
        // A throw while the request is aborted is a mid-flight cancellation, not a genuine
        // failure. Recording it as an error result would fake-complete the call (its
        // `result` becomes the abort message) and read as success on resume, so flag it
        // aborted instead and let the mapping step leave the call incomplete. Key off the
        // abort signal, not the error type: CoreToolBuilder wraps the AbortError in a
        // TOOL_EXECUTION_FAILED MastraError, so isAbortError(error) wouldn't match here.
        if (abortSignal?.aborted) {
          // Log the discarded error for observability (control flow unchanged).
          logger?.debug?.('Tool execution interrupted by request abort; leaving the tool call incomplete', {
            toolName: inputData.toolName,
            toolCallId: inputData.toolCallId,
            error: error instanceof Error ? error.message : String(error),
          });
          return {
            aborted: true,
            ...inputData,
          };
        }
        return {
          error: serializeToolError(error),
          ...inputData,
        };
      }
    },
  });
}
